import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { getBaseURL } from "../../services/api";

const API_URL = getBaseURL();

const TeamSquad = () => {
  const { shortCode } = useParams();
  const [players, setPlayers] = useState([]);
  const [form, setForm] = useState({ name: "", role: "BAT", credit: 9.0, image: "" });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamInfo, setTeamInfo] = useState(null);
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else { fetchPlayers(); fetchTeamInfo(); }
  }, [shortCode]);

  const fetchTeamInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/teams`, { headers: { Authorization: `Bearer ${token}` } });
      const team = res.data.find(t => t.short_code === shortCode.toUpperCase());
      if (team) setTeamInfo(team);
    } catch (err) { /* ignore */ }
  };

  const fetchPlayers = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/teams/${shortCode}/players`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlayers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API_URL}/admin/teams/players/${editing}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/admin/teams/${shortCode}/players`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setForm({ name: "", role: "BAT", credit: 9.0, image: "" });
      setEditing(null);
      fetchPlayers();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to save player");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} from squad?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/teams/players/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPlayers();
    } catch (err) { alert("Delete failed"); }
  };

  const getRoleName = (r) => {
    const map = { BAT: "BATSMAN", BOWL: "BOWLER", AR: "ALL ROUNDER", WK: "WICKET KEEPER" };
    return map[r] || r;
  };

  const getRoleColor = (r) => {
    const map = { BAT: "#3b82f6", BOWL: "#ef4444", AR: "#10b981", WK: "#f59e0b" };
    return map[r] || "#6366f1";
  };

  // Group players by role for display
  const grouped = { WK: [], BAT: [], AR: [], BOWL: [] };
  players.forEach(p => {
    const key = (p.role || "BAT").toUpperCase();
    if (grouped[key]) grouped[key].push(p);
    else grouped["BAT"].push(p);
  });

  const s = {
    page: { minHeight: "100vh", background: "#0a0e17", color: "#e2e8f0", fontFamily: "'Inter', 'Segoe UI', sans-serif", padding: "40px" },
    backBtn: { background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontWeight: 600, fontSize: "12px", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "20px", display: "flex", alignItems: "center", gap: "6px" },
    header: { display: "flex", alignItems: "center", gap: "16px", marginBottom: "36px" },
    teamLogo: { width: "48px", height: "48px", borderRadius: "50%", objectFit: "contain", background: "#111827", border: "2px solid #1e293b", padding: "4px" },
    title: { fontSize: "22px", fontWeight: 800, letterSpacing: "-0.5px", textTransform: "uppercase" },
    subtitle: { fontSize: "11px", color: "#6366f1", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase" },
    card: { background: "#111827", border: "1px solid #1e293b", borderRadius: "16px", padding: "28px", marginBottom: "36px" },
    formTitle: { fontSize: "12px", fontWeight: 700, color: "#818cf8", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "20px" },
    label: { fontSize: "11px", fontWeight: 600, color: "#64748b", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "6px", display: "block" },
    input: { width: "100%", padding: "12px 16px", background: "#0f172a", border: "1px solid #1e293b", borderRadius: "10px", color: "#e2e8f0", fontSize: "14px", outline: "none" },
    select: { width: "100%", padding: "12px 16px", background: "#0f172a", border: "1px solid #1e293b", borderRadius: "10px", color: "#e2e8f0", fontSize: "14px", outline: "none", appearance: "auto" },
    row: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px", marginBottom: "20px" },
    btnRow: { display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "16px" },
    btnCancel: { padding: "10px 24px", background: "transparent", border: "1px solid #374151", borderRadius: "10px", color: "#94a3b8", fontWeight: 600, fontSize: "13px", cursor: "pointer", textTransform: "uppercase" },
    btnSubmit: { padding: "10px 28px", background: "#6366f1", border: "none", borderRadius: "10px", color: "white", fontWeight: 700, fontSize: "13px", cursor: "pointer", textTransform: "uppercase" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "16px" },
    playerCard: { background: "#111827", border: "1px solid #1e293b", borderRadius: "14px", padding: "16px", display: "flex", alignItems: "center", gap: "14px", position: "relative", transition: "border-color 0.2s" },
    playerImg: { width: "44px", height: "44px", borderRadius: "50%", objectFit: "cover", background: "#0f172a", border: "2px solid #1e293b", flexShrink: 0 },
    playerName: { fontSize: "14px", fontWeight: 700, color: "#e2e8f0" },
    roleBadge: (role) => ({ fontSize: "9px", fontWeight: 700, color: getRoleColor(role), background: getRoleColor(role) + "15", border: `1px solid ${getRoleColor(role)}30`, padding: "2px 8px", borderRadius: "4px", letterSpacing: "1px", textTransform: "uppercase", position: "absolute", top: "10px", left: "12px" }),
    actionRow: { position: "absolute", top: "10px", right: "10px", display: "flex", gap: "4px" },
    miniBtn: { width: "26px", height: "26px", borderRadius: "6px", border: "1px solid #374151", background: "#0f172a", color: "#94a3b8", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }
  };

  return (
    <div style={s.page}>
      <button onClick={() => navigate("/winrox11-super-admin/manage-teams")} style={s.backBtn}>
        ← Back to Teams
      </button>

      <div style={s.header}>
        {teamInfo?.logo_url ? (
          <img src={teamInfo.logo_url} alt={shortCode} style={s.teamLogo} />
        ) : (
          <div style={{ ...s.teamLogo, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: 800, color: "#374151" }}>
            {shortCode.substring(0, 2)}
          </div>
        )}
        <div>
          <div style={s.title}>{teamInfo?.name || shortCode} Squad</div>
          <div style={s.subtitle}>{players.length} Total Cricketers</div>
        </div>
      </div>

      {/* ONBOARD CRICKETER FORM */}
      <div style={s.card}>
        <div style={s.formTitle}>{editing ? "Edit Cricketer" : "Onboard Cricketer"}</div>
        <form onSubmit={handleSubmit}>
          <div style={s.row}>
            <div>
              <label style={s.label}>Full Name</label>
              <input
                style={s.input}
                placeholder="Player full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label style={s.label}>Role</label>
              <select
                style={s.select}
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="BAT">Batsman</option>
                <option value="BOWL">Bowler</option>
                <option value="AR">All Rounder</option>
                <option value="WK">Wicket Keeper</option>
              </select>
            </div>
            <div>
              <label style={s.label}>🔗 Athlete Photo URL</label>
              <input
                style={s.input}
                placeholder="https://..."
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
              />
            </div>
          </div>
          <div style={s.btnRow}>
            {editing && (
              <button type="button" onClick={() => { setEditing(null); setForm({ name: "", role: "BAT", credit: 9.0, image: "" }); }} style={s.btnCancel}>Discard</button>
            )}
            <button type="submit" style={s.btnSubmit}>{editing ? "Update Player" : "Onboard Player"}</button>
          </div>
        </form>
      </div>

      {/* PLAYERS GRID */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px", color: "#475569" }}>Loading squad...</div>
      ) : players.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px", color: "#475569", background: "#111827", borderRadius: "16px", border: "1px solid #1e293b" }}>
          No players yet. Add your first cricketer above!
        </div>
      ) : (
        <div style={s.grid}>
          {players.map(p => (
            <div
              key={p.id}
              style={s.playerCard}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#374151"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#1e293b"; }}
            >
              <div style={s.roleBadge(p.role)}>{(p.role || "BAT").toUpperCase()}</div>
              
              <div style={s.actionRow}>
                <button
                  style={s.miniBtn}
                  onClick={() => {
                    setEditing(p.id);
                    setForm({ name: p.name, role: p.role, credit: parseFloat(p.credit) || 9, image: p.image || "" });
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >✏️</button>
                <button style={s.miniBtn} onClick={() => handleDelete(p.id, p.name)}>🗑️</button>
              </div>

              <div style={{ marginTop: "20px", display: "flex", alignItems: "center", gap: "12px", width: "100%" }}>
                {p.image ? (
                  <img src={p.image} alt={p.name} style={s.playerImg} onError={(e) => { e.target.src = "https://img.freepik.com/free-icon/cricket-player_318-80157.jpg"; }} />
                ) : (
                  <div style={{ ...s.playerImg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>🏏</div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={s.playerName}>{p.name}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamSquad;
